/**
 * useIdeaPool — Firestore CRUD hook for the Ideenpool feature.
 * Collection: idea_pool / documents per user (filtered by userId field).
 */
import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { Idea, IdeaStatus, IdeaEffort, IdeaImpact } from '../types';

// Post-it palette — six warm, distinct hues
export const POSTIT_COLORS = [
  '#fef08a', // yellow
  '#bbf7d0', // mint-green
  '#bfdbfe', // sky-blue
  '#fecaca', // rose
  '#e9d5ff', // lavender
  '#fed7aa', // peach
];

export function randomPostitColor() {
  return POSTIT_COLORS[Math.floor(Math.random() * POSTIT_COLORS.length)];
}

function docToIdea(docSnap: { id: string; data: () => Record<string, unknown> | undefined }): Idea {
  const d = (docSnap.data() || {}) as Partial<Idea>;
  
  const getMillis = (val: unknown) => {
    if (!val) return Date.now();
    if (typeof val === 'object' && val !== null && 'toMillis' in val && typeof (val as { toMillis: () => number }).toMillis === 'function') {
      return (val as { toMillis: () => number }).toMillis();
    }
    if (typeof val === 'object' && val !== null && 'seconds' in val) {
      const s = (val as { seconds: number; nanoseconds?: number }).seconds;
      const nano = (val as { seconds: number; nanoseconds?: number }).nanoseconds || 0;
      return s * 1000 + Math.floor(nano / 1000000);
    }
    if (val instanceof Date) {
      return val.getTime();
    }
    return typeof val === 'number' ? val : Date.now();
  };

  return {
    id: docSnap.id,
    userId: d.userId ?? '',
    title: d.title ?? '',
    summary: d.summary ?? '',
    body: d.body ?? '',
    source: d.source ?? 'manual',
    sourceRef: d.sourceRef,
    tags: d.tags ?? [],
    status: (d.status as IdeaStatus) ?? 'raw',
    effort: (d.effort as IdeaEffort) ?? 'medium',
    impact: (d.impact as IdeaImpact) ?? 'medium',
    createdAt: getMillis(d.createdAt),
    updatedAt: getMillis(d.updatedAt),
    promotedProjectId: d.promotedProjectId,
    color: d.color ?? randomPostitColor(),
    documents: d.documents ?? [],
    workspaceCategory: d.workspaceCategory ?? '',
    theme: d.theme ?? '',
    type: d.type ?? '',
    workspace: d.workspace ?? 'professional',
  };
}

export type NewIdeaPayload = Omit<Idea, 'id' | 'userId' | 'createdAt' | 'updatedAt'>;
export type UpdateIdeaPayload = Partial<Omit<Idea, 'id' | 'userId' | 'createdAt'>>;

export function useIdeaPool(userId: string | undefined, activeWorkspace?: 'professional' | 'private') {
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Real-time listener
  useEffect(() => {
    if (!userId || userId === 'e2e_local_uid') {
      setIdeas([]);
      setLoading(false);
      return;
    }

    // Query without orderBy to prevent composite index requirement in Firestore
    const q = query(
      collection(db, 'idea_pool'),
      where('userId', '==', userId)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        // Sort locally in memory by createdAt descending, filter by workspace
        const allIdeas = snap.docs.map(docToIdea).sort((a, b) => b.createdAt - a.createdAt);
        const filtered = activeWorkspace
          ? allIdeas.filter(idea => (idea.workspace ?? 'professional') === activeWorkspace)
          : allIdeas;
        setIdeas(filtered);
        setLoading(false);
      },
      (err) => {
        console.error('[useIdeaPool] snapshot error', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [userId, activeWorkspace]);

  /** Create a new idea, returns the new Firestore id */
  const addIdea = useCallback(
    async (payload: Partial<NewIdeaPayload> = {}) => {
      if (!userId) return null;
      const color = payload.color ?? randomPostitColor();
      const ref = await addDoc(collection(db, 'idea_pool'), {
        userId,
        title: payload.title ?? 'Neue Idee',
        summary: payload.summary ?? '',
        body: payload.body ?? '',
        source: payload.source ?? 'manual',
        sourceRef: payload.sourceRef ?? null,
        tags: payload.tags ?? [],
        status: payload.status ?? 'raw',
        effort: payload.effort ?? 'medium',
        impact: payload.impact ?? 'medium',
        color,
        documents: payload.documents ?? [],
        workspaceCategory: payload.workspaceCategory ?? '',
        theme: payload.theme ?? '',
        type: payload.type ?? '',
        workspace: payload.workspace ?? activeWorkspace ?? 'professional',
        createdAt: Date.now(),
        updatedAt: Date.now(),
      });
      return ref.id;
    },
    [userId, activeWorkspace]
  );

  /** Update fields on an existing idea */
  const updateIdea = useCallback(
    async (ideaId: string, updates: UpdateIdeaPayload) => {
      const ref = doc(db, 'idea_pool', ideaId);
      await updateDoc(ref, {
        ...updates,
        updatedAt: Date.now(),
      });
    },
    []
  );

  /** Permanently delete an idea */
  const deleteIdea = useCallback(async (ideaId: string) => {
    await deleteDoc(doc(db, 'idea_pool', ideaId));
  }, []);

  return { ideas, loading, error, addIdea, updateIdea, deleteIdea };
}
